import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createUser, getRole, updateUser } from "../services/userApi";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { Row, Spin } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import { uploadImage } from "../../Media/services/mediaApi";

const AddEditUserForm = ({ modal, setModal, toggle, selectedUser }) => {
  
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);

  const { isLoading: isLoadingRole, isError: isErrorRole, error, data: roles } = useQuery("roles", getRole);
  const category = "user";

  const handlePhotoFileUpdate = (fileItems) => {
    setImage(fileItems.length > 0 ? fileItems[0].file : null);
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("Oups!!! Veuillez renseigner le prénom"),
    lastName: Yup.string().required("Veuillez renseigner le nom"),
    email: Yup.string().email("Veuillez renseigner une adresse email valide").required("Veuillez renseigner l'adresse email"),
    phone: Yup.number().required("Son numéro de téléphone svp"),
    address: Yup.string().required("Veuillez renseigner l'adresse"),
    cniNumber: Yup.number().required("Veuillez renseigner le numéro CNI"),
    role: Yup.number().required("Veuillez lui attribuer un role"),
  });

  const initialValues = selectedUser ? {
    firstName: selectedUser.firstName,
    lastName: selectedUser.lastName,
    email: selectedUser.email,
    phone: selectedUser.phone,
    address: selectedUser.address || "",
    cniNumber: selectedUser.cniNumber || "",
    licenseNumber: selectedUser.licenseNumber || "",
    role: selectedUser.role || "",
    fileName: selectedUser.fileName || "",
  } : {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    cniNumber: "",
    licenseNumber: "",
    fileName: "",
    role: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        let fileName = values.fileName;
        
        if (image) {
          const response = await uploadImage(category, image);
          fileName = response?.name;
        }
        
        const userData = {
          ...values,
          fileName,
          role: { id: values.role}
        };
        if (selectedUser) {
          await updateUser(selectedUser?.id, userData);
          toast.success("L'utilisateur a été modifié avec succès 🎉");
        } else {
          await createUser(userData);
          toast.success("L'utilisateur a été créé avec succès 🎉");
        }

        await queryClient.invalidateQueries("users");
        toggle();
        formik.resetForm();
      } catch (error) {
        toast.error(`😌 Erreur: ${error?.message || error}`);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    formik.setValues(initialValues);
  }, [selectedUser]);

  const modalTitle = selectedUser ? "Édition" : `Ajout`;

  return (
    <Modal
      id="showModalClient"
      isOpen={modal}
      toggle={toggle}
      centered
      backdrop={"static"}
      style={{ maxWidth: "700px" }}
    >
      <ModalHeader className="bg-info-subtle p-3" toggle={toggle}>
        {modalTitle} d'un utilisateur <UserAddOutlined />
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col lg="12">
                <label htmlFor="fileName" className="form-label"> Photo profile </label> ( La photo de profile est optionnelle)
                <FilePond
                  file={image}
                  onupdatefiles={handlePhotoFileUpdate}
                  allowMultiple={false}
                  maxFiles={1}
                  name="fileName"
                  labelIdle='<span class="filepond--label-action">Parcourir</span> ou Glisser directement'
                />
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <div className="mb-3 pr-2">
                <label htmlFor="firstName" className="form-label"> Prénom <span className="text-danger">*</span> </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  required
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <div className="text-danger">{formik.errors.firstName}</div>
                )}
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <label htmlFor="lastName" className="form-label"> Nom </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className="text-danger">{formik.errors.lastName}</div>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <div className="mb-3 pr-2">
                <label htmlFor="email" className="form-label"> Email </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-danger">{formik.errors.email}</div>
                )}
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <label htmlFor="phone" className="form-label"> Téléphone </label>
                <input
                  type="number"
                  id="phone"
                  name="phone"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <div className="text-danger">{formik.errors.phone}</div>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <div className="mb-3 pr-2">
                <label htmlFor="address" className="form-label"> Adresse </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address}
                />
                {formik.touched.address && formik.errors.address && (
                  <div className="text-danger">{formik.errors.address}</div>
                )}
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <label htmlFor="cniNumber" className="form-label"> Numéro CNI </label>
                <input
                  type="number"
                  id="cniNumber"
                  name="cniNumber"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.cniNumber}
                />
                {formik.touched.cniNumber && formik.errors.cniNumber && (
                  <div className="text-danger">{formik.errors.cniNumber}</div>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <div className="mb-3 pr-2">
                <label htmlFor="licenseNumber" className="form-label"> Numéro de Permis </label>
                <input
                  type="text"
                  placeholder="Renseigner le permis pour les chauffeurs"
                  id="licenseNumber"
                  name="licenseNumber"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.licenseNumber}
                />
                {formik.touched.licenseNumber && formik.errors.licenseNumber && (
                  <div className="text-danger">{formik.errors.licenseNumber}</div>
                )}
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <label htmlFor="role" className="form-label"> Rôle </label>
                <select
                  id="role"
                  name="role"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.role}
                >
                  <option value="">Sélectionner un rôle</option>
                  {isLoadingRole ? (
                      <option disabled>Chargement des rôles, merci de patienter...</option>
                    ) : isErrorRole ? (
                      <option disabled>Erreur lors du chargement des rôles {error || error?.message}</option>
                    ) : (
                      roles?.map((role) => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))
                    )}
                </select>
                {formik.touched.role && formik.errors.role && (
                  <div className="text-danger">{formik.errors.role}</div>
                )}
              </div>
            </Col>
          </Row>
          
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>
            Annuler
          </Button>
          <Button type="submit" className="btn btn-soft-success" disabled={isLoading}>
            {isLoading ? <Spin size="small" /> : (selectedUser ? "Modifier" : "Ajouter")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddEditUserForm;
